import type { InterpretationModel } from '@/entities/optimization-statistic'
import type { UseQueryResult } from '@tanstack/react-query'

import React, { useMemo, useState } from 'react'
import { AlertTriangleIcon, EyeIcon, LockIcon } from 'lucide-react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { InterpretationContent } from '@/features/interpretation'

import {
	anchoringSchema,
	priceLimitsSchema,
	usePricingCampaignAnchoringExampleQuery,
	usePricingCampaignLimitsExampleQuery,
	usePricingCampaignRoundingExampleQuery,
} from '@/entities/pricing-campaign'
import { Access } from '@/entities/session'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { Badge } from '@/shared/components/ui/badge'
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from '@/shared/components/ui/sheet'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Spinner } from '@/shared/components/ui/spinner'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { ampli } from '@/shared/lib/ampli'
import { cn } from '@/shared/lib/utils'

export const PricingCampaignLimitsExampleSheet = () => {
	const formData = useWatch({
		name: 'settings.price_limits',
	})
	const isEnabled = useMemo(
		() => priceLimitsSchema.safeParse(formData).success,
		[formData]
	)

	const exampleQuery = usePricingCampaignLimitsExampleQuery(formData, {
		enabled: isEnabled,
	})

	return (
		<PricingCampaignSettingsExampleSheet
			exampleQuery={exampleQuery}
			isEnabled={isEnabled}
			type='price_limits'
		/>
	)
}

export const PricingCampaignAnchoringExampleSheet = () => {
	const formData = useWatch({
		name: 'settings.anchoring',
	})
	const isEnabled = useMemo(
		() => anchoringSchema.safeParse(formData).success,
		[formData]
	)

	const exampleQuery = usePricingCampaignAnchoringExampleQuery(formData, {
		enabled: isEnabled,
	})

	return (
		<PricingCampaignSettingsExampleSheet
			isEnabled={isEnabled}
			exampleQuery={exampleQuery}
			type='anchoring'
		/>
	)
}
export const PricingCampaignRoundingExampleSheet = () => {
	const formData = useWatch({
		name: 'settings.rounding',
	})

	const exampleQuery = usePricingCampaignRoundingExampleQuery(formData)

	return (
		<PricingCampaignSettingsExampleSheet
			isEnabled
			exampleQuery={exampleQuery}
			type='rounding'
		/>
	)
}
export const PricingCampaignSettingsExampleSheet = ({
	exampleQuery,
	type,
	isEnabled,
}: {
	/**
	 * @description Query result for the settings example
	 * @required
	 */
	exampleQuery: UseQueryResult<InterpretationModel, Error>

	/**
	 * @description Type of the settings example
	 * @required
	 */
	type: 'price_limits' | 'anchoring' | 'rounding'

	/**
	 * @description Whether the settings example is enabled
	 * @default false
	 */
	isEnabled?: boolean
}) => {
	const { t } = useTranslation()

	const [isOpen, setIsOpen] = useState<boolean>(false)
	if (!isEnabled) return null

	return (
		<Sheet open={isOpen} modal={false} onOpenChange={setIsOpen}>
			<Tooltip>
				<TooltipTrigger asChild>
					<SheetTrigger disabled={exampleQuery.isPending}>
						<Badge
							size='sm'
							variant='neutral'
							interactive
							className='relative'
							onClick={() =>
								ampli.pricingCampaignSettingsExampleClick({
									type,
								})
							}
						>
							{exampleQuery.isPending ? (
								<Spinner />
							) : exampleQuery.isError ? (
								<AlertTriangleIcon />
							) : (
								<EyeIcon />
							)}
							{t('pc.example.view', { defaultValue: 'View example' })}
						</Badge>
					</SheetTrigger>
				</TooltipTrigger>
				<TooltipContent>
					View example of the settings you have selected.
				</TooltipContent>
			</Tooltip>

			<SheetContent
				disbleOverlay
				className='absolute top-12 right-5 bottom-1 h-auto rounded-xl border bg-background/80 backdrop-blur-xl'
				container={document.getElementById('pc-modal-body')}
				onInteractOutside={(e) => e.preventDefault()}
			>
				<SheetHeader>
					<SheetTitle>
						{t('pc.example.title', { defaultValue: 'Setting example' })}
					</SheetTitle>
					<SheetDescription>
						{t('pc.example.description', {
							defaultValue: 'Example of the settings you have selected.',
						})}
					</SheetDescription>
				</SheetHeader>
				<ErrorBoundary>
					<div
						className={cn('flex w-full flex-col gap-4 overflow-y-auto px-4', {
							'opacity-50': exampleQuery.isPending,
						})}
					>
						<div className='flex w-full flex-col gap-4'>
							{exampleQuery.isError && (
								<Empty>
									<EmptyHeader>
										<EmptyMedia variant='icon' className='text-destructive'>
											<AlertTriangleIcon className='text-destructive' />
										</EmptyMedia>
										<EmptyTitle>
											{t('pc.example.error', {
												defaultValue: 'Error while fetching example',
											})}
										</EmptyTitle>
										<EmptyDescription>
											{t('pc.example.error_description', {
												defaultValue:
													'Looks like something went wrong while fetching the example. Please try again later or contact support if the problem persists.',
											})}
										</EmptyDescription>
									</EmptyHeader>
									<Access roles={['SuperUser']}>
										<EmptyContent>
											<code className='rounded-md bg-muted p-2 text-xs'>
												{exampleQuery.error.message}
											</code>
											<p className='inline-flex items-center gap-2 text-xs text-muted-foreground'>
												<LockIcon className='size-3' />
												Visible only for SuperUser
											</p>
										</EmptyContent>
									</Access>
								</Empty>
							)}
							{exampleQuery.isPending ? (
								<>
									<Skeleton className='h-4 w-32' />
									<Skeleton className='h-4 w-full' />
									<Skeleton className='h-4 w-52' />
									<Skeleton className='h-4 w-64' />
								</>
							) : (
								<InterpretationContent
									{...(exampleQuery.data || { children: [], data: undefined })}
								/>
							)}
						</div>
					</div>
				</ErrorBoundary>
			</SheetContent>
		</Sheet>
	)
}
